// src/components/wellfinder/LeaseName.jsx
//
// The lease name in a record's title position, and what to show when the
// registry has no lease name.
//
// ── WHY THERE IS A FALLBACK AT ALL ─────────────────────────────────────────
//
// 491 of 9,346 matched records — 5.3%, one every twenty — have an empty
// lease_name on the RRC well. That is not a defect: those registry records are
// genuinely sparse (API 10533395, a 1981 permit, has empty lease_name,
// operator, well_no, field, oil_gas_code and district_no). But better answers
// sit in the same props, so the route walks lease_name -> legal_location ->
// county and ships WHICH LEVEL ANSWERED as lease_name_source.
//
// ── THE TWO FALLBACKS DO NOT WANT THE SAME TREATMENT ───────────────────────
//
// Both are muted, because a value that is not a lease name must not read as
// one in the position an analyst scans for lease names. That is the same
// reasoning as the coverage strip's muted absent-state: the styling is the
// index into the meaning.
//
// They differ in what else they need:
//
//   legal_location  "Sec 15 / Block 49 / UL" — SELF-DESCRIBING. It cannot be
//                   mistaken for a lease name, so muting is the whole of it.
//
//   county          "CROCKETT" alone in the title position reads as a lease
//                   CALLED Crockett, which is a wrong answer rather than a
//                   quiet one. It gets " County" appended so the string itself
//                   says what kind of thing it is, not only how it is painted
//                   — colour is unavailable to a screen reader and to anyone
//                   copying the text out.
//
// The title attribute names the source in words at every level, because muted
// grey answers "this is not a lease name" and not "then what is it".

const LABEL = {
  lease: null,
  legal_location: "No lease name on the registry record — showing its legal location",
  county: "No lease name or legal location on the registry record — showing its county",
};

export default function LeaseName({ value, source, className = "" }) {
  // Nothing at any level. One record in the corpus is genuinely nameless
  // (RRC 37140522, whose props carry only api, has_lateral, status_norm,
  // status_source), so this string still has a job.
  if (!value) {
    return (
      <span className={`text-fg-secondary/70 italic ${className}`} title="The registry record carries no lease name, legal location or county">
        (unnamed lease)
      </span>
    );
  }

  // NO WRAPPER FOR A REAL LEASE NAME. Returning a bare string keeps the text
  // node contiguous with whatever the caller renders beside it -- QueueRow puts
  // the well number straight after, and an extra <span> split "UNIVERSITY FJ 1H"
  // into two elements, which is invisible on screen and breaks any text query
  // spanning the pair. Markup is added only where there is something to mark.
  if (source === "lease" || !source) return value;

  const text = source === "county" ? `${value} County` : value;
  return (
    <span className={`text-fg-secondary ${className}`} title={LABEL[source] ?? undefined}>
      {text}
    </span>
  );
}
